import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import * as echarts from 'echarts';
import { useIntl } from 'umi';
import SvgIcon from '@home/components/SvgIcon';
import './index.scss';

export type TStudioFullscreenTargetId = string | symbol;

export interface IStudioFullscreenTarget {
  element: HTMLElement;
  ready: boolean;
  active?: boolean;
  priority?: number;
}

interface IStudioFullscreenContext {
  isFullscreen: boolean;
  registerTarget: (
    id: TStudioFullscreenTargetId,
    target: IStudioFullscreenTarget,
  ) => void;
  unregisterTarget: (id: TStudioFullscreenTargetId) => void;
}

const BODY_FULLSCREEN_CLASSES = [
  'databrain-body-studio-fullscreen',
  'databrain-body-dbh-studio-fullscreen',
];
const BODY_TRIGGER_VISIBLE_CLASS = 'databrain-body-studio-fullscreen-trigger-visible';
const TARGET_ACTIVE_CLASS = 'dbh-studio-fullscreen-target--active';
const TARGET_BACKGROUND_PROPERTY = '--dbh-studio-fullscreen-background';

const getEffectiveBackgroundColor = (element: HTMLElement): string => {
  let currentElement: HTMLElement | null = element;

  while (currentElement) {
    const backgroundColor = window.getComputedStyle(currentElement).backgroundColor;
    const normalizedColor = backgroundColor.split(' ').join('').toLowerCase();
    if (
      backgroundColor
      && normalizedColor !== 'transparent'
      && normalizedColor !== 'rgba(0,0,0,0)'
    ) {
      return backgroundColor;
    }
    currentElement = currentElement.parentElement;
  }

  return '#fff';
};

const clearFullscreenTargetStyle = (element?: HTMLElement | null) => {
  element?.classList.remove(TARGET_ACTIVE_CLASS);
  element?.style.removeProperty(TARGET_BACKGROUND_PROPERTY);
};

const MACOS_FULLSCREEN_TRANSITION_MS = 300;

const createViewportResizeScheduler = () => {
  let rafIds: number[] = [];
  let timeoutIds: number[] = [];
  let scheduleToken = 0;

  const cancel = (): void => {
    scheduleToken += 1;
    rafIds.forEach((id) => window.cancelAnimationFrame(id));
    timeoutIds.forEach((id) => window.clearTimeout(id));
    rafIds = [];
    timeoutIds = [];
  };

  const schedule = (target?: HTMLElement | null): void => {
    cancel();
    const currentToken = scheduleToken;
    const resize = (): void => {
      if (currentToken !== scheduleToken) return;
      window.dispatchEvent(new Event('resize'));
      target?.querySelectorAll<HTMLElement>('.echarts-for-react').forEach((element) => {
        echarts.getInstanceByDom(element)?.resize({ width: 'auto', height: 'auto' });
      });
    };

    if (typeof window.requestAnimationFrame === 'function') {
      const outerRafId = window.requestAnimationFrame(() => {
        if (currentToken !== scheduleToken) return;
        const innerRafId = window.requestAnimationFrame(resize);
        rafIds.push(innerRafId);
      });
      rafIds.push(outerRafId);
    } else {
      timeoutIds.push(window.setTimeout(resize, 0));
    }

    // macOS 原生全屏存在过渡动画，再补一次尺寸同步以取得最终视口宽度。
    timeoutIds.push(window.setTimeout(resize, MACOS_FULLSCREEN_TRANSITION_MS));
  };

  return { schedule, cancel };
};

const StudioFullscreenContext = createContext<IStudioFullscreenContext | null>(null);
StudioFullscreenContext.displayName = 'StudioFullscreenContext';

export const useStudioFullscreen = () => useContext(StudioFullscreenContext);

export const StudioFullscreenProvider: React.FC<{ children?: React.ReactNode; }> = ({
  children,
}) => {
  const { formatMessage } = useIntl();
  const targetsRef = useRef(new Map<TStudioFullscreenTargetId, IStudioFullscreenTarget>());
  const fullscreenTargetRef = useRef<IStudioFullscreenTarget | null>(null);
  const viewportResizeSchedulerRef = useRef(createViewportResizeScheduler());
  const [activeTarget, setActiveTarget] = useState<IStudioFullscreenTarget | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const scheduleViewportResize = useCallback((target?: HTMLElement | null): void => {
    viewportResizeSchedulerRef.current.schedule(target);
  }, []);

  const clearFullscreen = useCallback(() => {
    clearFullscreenTargetStyle(fullscreenTargetRef.current?.element);
    fullscreenTargetRef.current = null;
    document.body.classList.remove(...BODY_FULLSCREEN_CLASSES);
    setIsFullscreen(false);
  }, []);

  const syncActiveTarget = useCallback(() => {
    const readyTargets = Array.from(targetsRef.current.values())
      .filter((target) => target.active !== false && target.ready);
    if (!readyTargets.length) {
      setActiveTarget(null);
      return;
    }
    const maxPriority = readyTargets.reduce(
      (max, target) => Math.max(max, target.priority ?? 0),
      Number.NEGATIVE_INFINITY,
    );
    const nextTarget = readyTargets
      .filter((target) => (target.priority ?? 0) === maxPriority)
      .pop() ?? null;
    setActiveTarget(nextTarget);
  }, []);

  const registerTarget = useCallback((
    id: TStudioFullscreenTargetId,
    target: IStudioFullscreenTarget,
  ) => {
    targetsRef.current.set(id, target);
    syncActiveTarget();
  }, [syncActiveTarget]);

  const unregisterTarget = useCallback((id: TStudioFullscreenTargetId) => {
    const target = targetsRef.current.get(id);
    clearFullscreenTargetStyle(target?.element);
    targetsRef.current.delete(id);
    syncActiveTarget();

    if (fullscreenTargetRef.current?.element !== target?.element) return;
    if (document.fullscreenElement && document.exitFullscreen) {
      void document.exitFullscreen().catch(clearFullscreen);
      return;
    }
    clearFullscreen();
  }, [clearFullscreen, syncActiveTarget]);

  const enterFullscreen = useCallback(async () => {
    if (!activeTarget?.ready) return;

    fullscreenTargetRef.current = activeTarget;
    activeTarget.element.style.setProperty(
      TARGET_BACKGROUND_PROPERTY,
      getEffectiveBackgroundColor(activeTarget.element),
    );
    activeTarget.element.classList.add(TARGET_ACTIVE_CLASS);
    document.body.classList.add(...BODY_FULLSCREEN_CLASSES);
    setIsFullscreen(true);

    try {
      if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      }
    } catch (_) {
      // 浏览器拒绝原生全屏时保留 CSS 全屏降级。
    }
  }, [activeTarget]);

  const exitFullscreen = useCallback(async () => {
    const fullscreenTarget = fullscreenTargetRef.current?.element;
    if (document.fullscreenElement && document.exitFullscreen) {
      try {
        await document.exitFullscreen();
      } catch (_) {
        clearFullscreen();
        scheduleViewportResize(fullscreenTarget);
      }
      return;
    }
    clearFullscreen();
    scheduleViewportResize(fullscreenTarget);
  }, [clearFullscreen, scheduleViewportResize]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenTarget = fullscreenTargetRef.current?.element;
      if (!document.fullscreenElement) clearFullscreen();
      scheduleViewportResize(fullscreenTarget);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [clearFullscreen, scheduleViewportResize]);

  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !document.fullscreenElement) {
        clearFullscreen();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [clearFullscreen, isFullscreen]);

  useEffect(() => {
    if (
      isFullscreen
      && fullscreenTargetRef.current
      && fullscreenTargetRef.current.element !== activeTarget?.element
    ) {
      void exitFullscreen();
    }
  }, [activeTarget, exitFullscreen, isFullscreen]);

  useEffect(() => {
    const visible = Boolean(activeTarget?.ready || isFullscreen);
    document.body.classList.toggle(BODY_TRIGGER_VISIBLE_CLASS, visible);
    return () => document.body.classList.remove(BODY_TRIGGER_VISIBLE_CLASS);
  }, [activeTarget, isFullscreen]);

  useEffect(() => () => {
    viewportResizeSchedulerRef.current.cancel();
    clearFullscreenTargetStyle(fullscreenTargetRef.current?.element);
    document.body.classList.remove(
      ...BODY_FULLSCREEN_CLASSES,
      BODY_TRIGGER_VISIBLE_CLASS,
    );
    if (document.fullscreenElement && document.exitFullscreen) {
      void document.exitFullscreen().catch(() => undefined);
    }
  }, []);

  const contextValue = useMemo(() => ({
    isFullscreen,
    registerTarget,
    unregisterTarget,
  }), [isFullscreen, registerTarget, unregisterTarget]);

  const floatingButton = (activeTarget?.ready || isFullscreen) && typeof document !== 'undefined'
    ? createPortal(
      <button
        type="button"
        className="dbh-studio-floating-fullscreen"
        aria-label={formatMessage({
          id: isFullscreen
            ? 'dbh_studio_fullscreen_exit'
            : 'dbh_studio_fullscreen_enter',
        })}
        onClick={() => {
          void (isFullscreen ? exitFullscreen() : enterFullscreen());
        }}
      >
        <SvgIcon
          className="dbh-studio-floating-fullscreen__icon"
          // type={isFullscreen ? 'studio-scale' : 'studio-narrow'}
          type={isFullscreen ? 'cancel_full_screen' : 'full_screen'}
        />
      </button>,
      document.body,
    )
    : null;

  return (
    <StudioFullscreenContext.Provider value={contextValue}>
      {children}
      {floatingButton}
    </StudioFullscreenContext.Provider>
  );
};
